<script setup lang="ts">
const props = defineProps({
  password: {
    type: String,
    required: true,
  },
  compactMode: {
    type: Boolean,
    default: false,
  },
});

const { t } = useTexts();

const { validationData } = storeToRefs(useRegisterStore());

const strengthItems = ref([
  {
    id: 1,
    text: t('account.security.at_least_1_letter'),
    check: false,
  },
  {
    id: 2,
    text: t('account.security.at_least_1_number'),
    check: false,
  },
  {
    id: 3,
    text: t('account.security.at_least_1_special_character'),
    check: false,
  },
  {
    id: 4,
    text: t('account.security.at_least_8_characters'),
    check: false,
  },
]);

const hasLetter = computed(() => {
  return props.password?.match(/[a-z]+/) || props.password?.match(/[A-Z]+/);
});

function updatePasswordStrength() {
  if (strengthItems.value[0]) {
    strengthItems.value[0].check = !!hasLetter.value;
  }
  if (strengthItems.value[1]) {
    strengthItems.value[1].check = !!props.password?.match(/[0-9]+/);
  }
  if (strengthItems.value[2]) {
    strengthItems.value[2].check =
      !!props.password?.match(/[!@#$%^&*)(+=._-]/g);
  }
  if (strengthItems.value[3]) {
    strengthItems.value[3].check = (props.password?.length ?? 0) >= 8;
  }
}

const passwordStrengthBar = computed(() => {
  const strength = strengthItems.value.filter((item) => item.check).length;

  const missingItems = (1 - strength / strengthItems.value.length) * 100;

  if (validationData.value?.fields) {
    validationData.value.fields.password = !missingItems;
  }

  return missingItems + '%';
});

onMounted(() => {
  nextTick(updatePasswordStrength);
  if (!props.password && validationData.value?.fields) {
    validationData.value.fields.password = false;
  }
});

defineExpose({
  updatePasswordStrength,
  passwordIsValid: computed(
    () => validationData.value?.fields?.password ?? false,
  ),
});
</script>

<template>
  <section>
    <div
      v-if="!compactMode"
      :class="$style.strengthBarContainer"
    >
      <div :class="$style.strengthBar" />
      <span class="w-1/2" />
      <span class="w-1/4" />
      <span class="w-3/4" />
    </div>

    <div :class="[$style.strengthItems, { [$style.compact]: compactMode }]">
      <span
        v-for="item in strengthItems"
        :key="item.id"
      >
        <NuxtIcon
          v-if="item.check"
          class="text-success"
          name="check"
        />
        <NuxtIcon
          v-else
          class="text-error"
          name="close"
        />
        {{ item.text }}</span
      >
    </div>
  </section>
</template>

<style module lang="scss">
.strengthBarContainer {
  @apply relative my-0.5 w-full rounded-full h-2 bg-gradient-to-r from-[#b91c1c] via-[#eab308] to-[#15803d];

  .strengthBar {
    @apply absolute top-0 right-0 h-2 transition-all ease-out duration-1000;
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(0, 0, 0, 0.6) 7%,
      rgba(0, 0, 0, 0.6) 100%
    );
    width: v-bind(passwordStrengthBar);
  }

  span {
    @apply absolute top-0 left-0 h-1.5 border-solid border-r-2 border-white/10 mt-[0.0685rem];
  }
}

.strengthItems {
  @apply mt-5 flex flex-wrap flex-col gap-x-5 gap-y-3 text-white text-xs;

  span {
    @apply flex items-center gap-1.5;
  }

  &.compact {
    @apply flex-row mt-0 gap-x-3 gap-y-2 text-2xs;

    span {
      @apply gap-1;
    }
  }
}
</style>
